import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ShoppingBag, Minus, Plus, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      setSize(data.sizes[1] || data.sizes[0]);
      setColor(data.colors[0]);
    }).catch(() => navigate("/shop"));
  }, [id, navigate]);

  if (!product) return <div className="min-h-[60vh] grid place-items-center font-display font-bold text-xl animate-pulse">Loading…</div>;

  const handleAdd = () => {
    addItem({
      key: `${product.product_id}-${size}-${color}`,
      product_id: product.product_id,
      name: product.name,
      image: product.image,
      price: product.price,
      size, color, quantity: qty,
    });
    toast.success("Added to cart", { description: `${product.name} · ${size} · ${color}` });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="product-detail-page">
      <Link to="/shop" className="inline-flex items-center gap-2 font-bold mb-8 hover:text-brand-pink">
        <ArrowLeft className="w-4 h-4" /> Back to shop
      </Link>
      <div className="grid gap-10 lg:grid-cols-2">
        <div className="relative">
          <div className="absolute -top-3 -left-3 w-full h-full bg-brand-yellow rounded-3xl border-2 border-ink" />
          <img src={product.image} alt={product.name} className="relative w-full aspect-square object-cover rounded-3xl border-2 border-ink" data-testid="product-image" />
        </div>

        <div>
          {product.badge && <span className="inline-block bg-brand-pink neo-sm rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider mb-3">{product.badge}</span>}
          <h1 className="font-display font-bold text-4xl sm:text-5xl leading-tight" data-testid="product-name">{product.name}</h1>
          <p className="mt-3 font-display font-bold text-3xl bg-brand-yellow neo-sm rounded-lg inline-block px-3 py-1">${product.price.toFixed(2)}</p>
          <p className="mt-5 font-semibold text-ink/70 text-lg">{product.description}</p>

          <div className="mt-7">
            <p className="text-xs font-bold uppercase tracking-widest mb-3">Color · <span className="text-ink/60">{color}</span></p>
            <div className="flex flex-wrap gap-3">
              {product.colors.map((c) => (
                <button key={c} onClick={() => setColor(c)} data-testid={`color-${c}`}
                  className={`px-4 py-2 rounded-lg font-bold border-2 border-ink neo-press ${color === c ? "bg-ink text-paper" : "bg-white"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <p className="text-xs font-bold uppercase tracking-widest mb-3">Size</p>
            <div className="flex flex-wrap gap-3">
              {product.sizes.map((s) => (
                <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                  className={`w-12 h-12 rounded-lg font-bold border-2 border-ink neo-press ${size === s ? "bg-ink text-paper" : "bg-white"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <div className="flex items-center bg-white neo rounded-xl overflow-hidden">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-brand-yellow" data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
              <span className="w-12 text-center font-display font-bold text-lg" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-3 hover:bg-brand-yellow" data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={handleAdd} className="flex-1 min-w-[200px] inline-flex items-center justify-center gap-2 bg-ink text-paper font-bold px-6 py-3.5 rounded-xl neo-press border-2 border-ink" data-testid="add-to-cart-btn">
              <ShoppingBag className="w-5 h-5" /> Add to Cart
            </button>
          </div>

          <div className="mt-8 grid gap-2 font-semibold text-ink/70">
            {["Free shipping over $50", "30-day easy returns", "Ethically made, organic cotton"].map((f) => (
              <div key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-pink" /> {f}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
